import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PencilIcon } from '@heroicons/react/20/solid';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { Heading } from '../../../../components/Heading';
import { Badge } from '../../../../components/Badge';
import { Button } from '../../../../components/Button';
import { Text } from '../../../../components/Text';
import Loading from '../../../../components/Loading';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../../components/Table';
import { Card } from '../../../../components/Card';
import { ListToolbarRow, ListToolbarSearch } from '../../../../components/ListToolbar';
import Pagination from '../../../../components/Pagination';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { useAiReportDatasets } from '../../../../hooks/useAiReportDataset';
import { AiReportDataset } from '../../../../models/AiReportDataset';

const PAGE_SIZE = 10;

const ViewDatasetPage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const datasetId = Number(id);
    const { fetchDatasetById } = useAiReportDatasets();

    const [dataset, setDataset] = useState<AiReportDataset | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const fetched = await fetchDatasetById(datasetId);
                setDataset(fetched);
            } catch (err) {
                showErrorToast('Failed to fetch data set');
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasetId]);

    const rows = useMemo(() => dataset?.rows || [], [dataset]);
    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((row) =>
            Object.values(row.row_data || {}).some((v) => String(v ?? '').toLowerCase().includes(q))
        );
    }, [rows, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    if (loading) return <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>;
    if (!dataset) return <p>Data set not found.</p>;

    const referredColumn = dataset.columns.find((c) => c.key === dataset.referred_column_key);
    const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
    const pageItems = filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <div className="mx-auto max-w-6xl">
            <Link
                to="/gp/ai-report-helper/datasets"
                className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300"
            >
                <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                Back to Data Sets
            </Link>
            <div className="mb-5 mt-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Heading>{dataset.name}</Heading>
                    {referredColumn && <Badge color="violet">Referred: {referredColumn.label}</Badge>}
                </div>
                <Button outline onClick={() => navigate(`/gp/ai-report-helper/datasets/edit/${dataset.id}`)}>
                    <PencilIcon />
                    Edit
                </Button>
            </div>
            <Text className="mb-4 text-xs text-zinc-500">
                {dataset.columns.length} columns &middot; {rows.length} row{rows.length === 1 ? '' : 's'}
            </Text>

            {rows.length === 0 ? (
                <p className="py-16 text-center">No rows yet.</p>
            ) : (
                <Card className="p-6">
                    <ListToolbarRow search={<ListToolbarSearch value={search} onChange={setSearch} placeholder="Search rows…" />}>
                        {null}
                    </ListToolbarRow>

                    {filteredRows.length === 0 ? (
                        <p className="py-16 text-center">No rows match your search.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <Table grid striped dense className="[--gutter:theme(spacing.4)]">
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader className="w-12 bg-zinc-950/[0.03] text-center dark:bg-white/[0.03]">#</TableHeader>
                                            {dataset.columns.map((c) => (
                                                <TableHeader key={c.key} className="bg-zinc-950/[0.03] dark:bg-white/[0.03]">
                                                    {c.label}
                                                    {c.key === dataset.referred_column_key && <span className="ml-1 text-violet-600">&bull;</span>}
                                                </TableHeader>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pageItems.map((row, idx) => (
                                            <TableRow key={row.id}>
                                                <TableCell className="text-center text-zinc-400">
                                                    {(currentPage - 1) * PAGE_SIZE + idx + 1}
                                                </TableCell>
                                                {dataset.columns.map((c) => (
                                                    <TableCell key={c.key}>{row.row_data[c.key] || ''}</TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                totalResult={filteredRows.length}
                                dataName="rows"
                                pageSize={PAGE_SIZE}
                            />
                        </>
                    )}
                </Card>
            )}
        </div>
    );
};

export default ViewDatasetPage;
